"use client";

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	type ReactNode,
} from "react";

const DEV_MODE_STORAGE_KEY = "vastipost-dev-mode";

interface DevModeContextValue {
	/** When true, shows phone mockup frame on desktop. When false, uses responsive layout. */
	isPhoneMockupMode: boolean;
	/** Toggle between phone mockup and responsive modes */
	toggleDevMode: () => void;
	/** Set dev mode explicitly */
	setPhoneMockupMode: (enabled: boolean) => void;
	/** Whether we've loaded the preference from storage */
	isLoaded: boolean;
}

const DevModeContext = createContext<DevModeContextValue | null>(null);

export function DevModeProvider({ children }: { children: ReactNode }) {
	// Default to phone mockup mode for development
	const [isPhoneMockupMode, setIsPhoneMockupMode] = useState(true);
	const [isLoaded, setIsLoaded] = useState(false);

	// Load preference from localStorage on mount
	useEffect(() => {
		try {
			const stored = localStorage.getItem(DEV_MODE_STORAGE_KEY);
			if (stored !== null) {
				setIsPhoneMockupMode(stored === "true");
			}
		} catch {
			// localStorage not available (SSR or private browsing)
		}
		setIsLoaded(true);
	}, []);

	// Persist preference to localStorage
	useEffect(() => {
		if (!isLoaded) return;
		try {
			localStorage.setItem(DEV_MODE_STORAGE_KEY, String(isPhoneMockupMode));
		} catch {
			// localStorage not available
		}
	}, [isPhoneMockupMode, isLoaded]);

	const toggleDevMode = useCallback(() => {
		setIsPhoneMockupMode((prev) => !prev);
	}, []);

	const setPhoneMockupMode = useCallback((enabled: boolean) => {
		setIsPhoneMockupMode(enabled);
	}, []);

	return (
		<DevModeContext.Provider
			value={{
				isPhoneMockupMode,
				toggleDevMode,
				setPhoneMockupMode,
				isLoaded,
			}}
		>
			{children}
		</DevModeContext.Provider>
	);
}

export function useDevMode() {
	const context = useContext(DevModeContext);
	if (!context) {
		throw new Error("useDevMode must be used within a DevModeProvider");
	}
	return context;
}

/**
 * Hook to check if we're in standalone PWA mode (installed to home screen)
 */
export function useIsPWA() {
	const [isPWA, setIsPWA] = useState(false);

	useEffect(() => {
		// Check if running as installed PWA
		const isStandalone =
			window.matchMedia("(display-mode: standalone)").matches ||
			// iOS Safari
			("standalone" in window.navigator &&
				(window.navigator as { standalone?: boolean }).standalone === true);

		setIsPWA(isStandalone);

		// Listen for changes (in case user installs while viewing)
		const mediaQuery = window.matchMedia("(display-mode: standalone)");
		const handler = (e: MediaQueryListEvent) => setIsPWA(e.matches);
		mediaQuery.addEventListener("change", handler);

		return () => mediaQuery.removeEventListener("change", handler);
	}, []);

	return isPWA;
}
